import { useState, useCallback } from 'react';
import { Outlet, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROLE_LABELS, ROLES } from '../utils/roles';

const PAGE_TITLES = {
  '/admin': 'الرئيسية',
  '/decks/admin': 'حزم',
  '/journeys/admin': 'الرحلات التعليمية',
  '/admin/classes': 'إدارة المواد',
  '/admin/reports': 'بلاغات الأخطاء',
  '/admin/students': 'إدارة الطلاب',
  '/admin/users': 'إدارة المشرفين',
  '/admin/student-stats': 'إحصائيات الطلاب',
  '/admin/subject-stats': 'إحصائيات المواد',
  '/admin/notifications': 'إدارة الإشعارات',
  '/admin/monitoring': 'مراقبة الموقع',
  '/admin/settings': 'إعدادات الموقع',
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/decks/admin/')) return 'تعديل المجموعة';
  if (pathname.startsWith('/journeys/admin/')) return 'تعديل الرحلة';
  if (pathname.startsWith('/admin/student-stats/')) return 'تفاصيل الطالب';
  if (pathname.startsWith('/admin/subject-stats/')) return 'تفاصيل المادة';
  return 'لوحة التحكم';
}

/* ── Inline SVG icons (Feather-style) ── */
function NavIcon({ type }) {
  const p = {
    className: 'admin-sidebar-icon',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (type) {
    case 'home':
      return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>;
    case 'layers':
      return <svg {...p}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
    case 'map':
      return <svg {...p}><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>;
    case 'book':
      return <svg {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>;
    case 'flag':
      return <svg {...p}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;
    case 'users':
      return <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
    case 'shield':
      return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case 'barChart':
      return <svg {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
    case 'trending':
      return <svg {...p}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
    case 'bell':
      return <svg {...p}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
    case 'activity':
      return <svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
    case 'settings':
      return <svg {...p}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>;
    default:
      return null;
  }
}

/* ── Sidebar navigation structure ── */
const NAV_SECTIONS = [
  {
    id: 'main',
    items: [
      { label: 'الرئيسية', path: '/admin', end: true, icon: 'home', roles: 'all' },
    ],
  },
  {
    id: 'content',
    label: 'المحتوى التعليمي',
    items: [
      { label: 'حزم', path: '/decks/admin', icon: 'layers', roles: 'all' },
      { label: 'الرحلات التعليمية', path: '/journeys/admin', icon: 'map', roles: 'all' },
      { label: 'إدارة المواد', path: '/admin/classes', icon: 'book', roles: 'adminUp' },
      { label: 'بلاغات الأخطاء', path: '/admin/reports', icon: 'flag', roles: 'adminUp' },
    ],
  },
  {
    id: 'users',
    label: 'المستخدمون',
    items: [
      { label: 'إدارة الطلاب', path: '/admin/students', icon: 'users', roles: 'adminUp' },
      { label: 'إدارة المشرفين', path: '/admin/users', icon: 'shield', roles: 'superAdmin' },
    ],
  },
  {
    id: 'analytics',
    label: 'التحليلات',
    items: [
      { label: 'إحصائيات الطلاب', path: '/admin/student-stats', icon: 'barChart', roles: 'adminUp' },
      { label: 'إحصائيات المواد', path: '/admin/subject-stats', icon: 'trending', roles: 'adminUp' },
    ],
  },
  {
    id: 'operations',
    label: 'التشغيل والمتابعة',
    items: [
      { label: 'إدارة الإشعارات', path: '/admin/notifications', icon: 'bell', roles: 'adminUp' },
      { label: 'مراقبة الموقع', path: '/admin/monitoring', icon: 'activity', roles: 'adminUp' },
    ],
  },
  {
    id: 'system',
    label: 'النظام',
    items: [
      { label: 'إعدادات الموقع', path: '/admin/settings', icon: 'settings', roles: 'adminUp' },
    ],
  },
];

function canSeeItem(item, role) {
  if (item.roles === 'all') return true;
  if (item.roles === 'adminUp') return role === ROLES.SUPER_ADMIN || role === ROLES.ADMIN;
  if (item.roles === 'superAdmin') return role === ROLES.SUPER_ADMIN;
  return false;
}

export default function AdminLayout() {
  const { user, role, displayName, loading, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 900);
  const [collapsed, setCollapsed] = useState({});

  const toggleSection = useCallback((id) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleNavClick = useCallback(() => {
    if (window.innerWidth <= 900) setSidebarOpen(false);
  }, []);

  if (loading) return <div className="loading-screen">جاري التحميل...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const pageTitle = getPageTitle(pathname);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`admin-layout${!sidebarOpen ? ' sidebar-collapsed' : ''}`}>
      {/* Mobile overlay */}
      <div
        className={`admin-sidebar-overlay${sidebarOpen ? ' open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="admin-sidebar-brand">
          <h2>بذكاء</h2>
          <span>لوحة التحكم</span>
        </div>

        <nav className="admin-sidebar-nav">
          {NAV_SECTIONS.map(section => {
            const visible = section.items.filter(item => canSeeItem(item, role));
            if (visible.length === 0) return null;
            const isCollapsed = collapsed[section.id];

            return (
              <div key={section.id} className="admin-sidebar-section">
                {section.label && (
                  <button
                    className="admin-sidebar-section-label"
                    onClick={() => toggleSection(section.id)}
                    type="button"
                  >
                    <span>{section.label}</span>
                    <svg
                      className={`admin-sidebar-chevron${isCollapsed ? ' collapsed' : ''}`}
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                )}

                {!isCollapsed && visible.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end || false}
                    className={({ isActive }) => `admin-sidebar-item${isActive ? ' active' : ''}`}
                    onClick={handleNavClick}
                  >
                    <NavIcon type={item.icon} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <a href="https://naseehamath.com/" target="_blank" rel="noopener noreferrer">
            <img src="/images/logo.png" alt="النصيحة التعليمي" className="admin-sidebar-footer-logo" />
          </a>
        </div>
      </aside>

      {/* Main area */}
      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-start">
            <button
              className="admin-sidebar-toggle"
              onClick={() => setSidebarOpen(prev => !prev)}
              type="button"
              aria-label="القائمة"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <h1 className="admin-topbar-title">{pageTitle}</h1>
          </div>

          <div className="admin-topbar-end">
            <div className="admin-topbar-user">
              <span className="admin-topbar-name">{displayName || user.email}</span>
              {role && <span className="role-badge">{ROLE_LABELS[role] || role}</span>}
            </div>
            <button className="btn btn-sm btn-secondary" onClick={handleLogout}>خروج</button>
          </div>
        </header>

        <main className="admin-content-area">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
